import { useEffect, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function Field({ label, icon: Icon, error, textarea, right, testid, className = "", ...props }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className={`block ${className}`} data-testid={testid ? `${testid}-field` : undefined}>
      {label && <span className="t-label">{label}</span>}
      <span className="relative block">
        {Icon && <Icon className="absolute left-4 top-[15px] w-5 h-5 text-[#64748B] pointer-events-none" strokeWidth={1.8} />}
        <Tag className={`t-input ${Icon ? "pl-12" : ""} ${right ? "pr-12" : ""} ${error ? "border-[#DC2626]" : ""}`} data-testid={testid} {...props} />
        {right && <span className="absolute right-2 top-1.5">{right}</span>}
      </span>
      {error && <span className="block mt-1.5 text-[13px] text-[#DC2626]" data-testid={testid ? `${testid}-error` : undefined}>{error}</span>}
    </label>
  );
}

const BTN_VARIANTS = { primary: "t-btn-primary", outline: "t-btn-outline", ghost: "t-btn-ghost", danger: "t-btn-danger" };

export function Btn({ variant = "primary", loading, children, className = "", testid, ...props }) {
  return (
    <button className={`t-btn ${BTN_VARIANTS[variant] ?? ""} ${className}`} disabled={loading || props.disabled} data-testid={testid} {...props}>
      {loading && <Loader2 className="w-5 h-5 animate-spin" />}
      {children}
    </button>
  );
}

export const Chip = ({ selected, children, testid, ...p }) => (
  <button type="button" className={`t-chip ${selected ? "t-chip-on" : ""}`} data-testid={testid} {...p}>{children}</button>
);

export function TopBar({ title, right, backTo, testid = "topbar-back", align = "center", standalone }) {
  const nav = useNavigate();
  return (
    <div className={`sticky top-0 ${standalone ? "" : "lg:top-[72px]"} z-20 bg-white flex items-center gap-2 h-14 -mx-5 px-3 lg:mx-0 lg:px-0`}>
      <button className="w-10 h-10 inline-flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={() => (backTo ? nav(backTo) : nav(-1))} data-testid={testid} aria-label="Go back">
        <ChevronLeft className="w-6 h-6" strokeWidth={2.4} />
      </button>
      <h1 className={`flex-1 ${align === "left" ? "text-left pl-1" : "text-center"} text-[18px] font-bold truncate`} data-testid="topbar-title">{title}</h1>
      <div className="min-w-10 flex items-center justify-end gap-1">{right}</div>
    </div>
  );
}

export const Spinner = ({ className = "" }) => (
  <div className={`flex justify-center py-16 ${className}`} data-testid="loading-spinner"><Loader2 className="w-7 h-7 animate-spin text-[#0000EC]" /></div>
);

export function Empty({ icon: Icon, title, text, action, testid }) {
  return (
    <div className="flex flex-col items-center text-center py-14 px-6" data-testid={testid}>
      {Icon && <div className="w-16 h-16 rounded-2xl bg-[#E5E5FD] flex items-center justify-center mb-4"><Icon className="w-8 h-8 text-[#0000EC]" /></div>}
      <p className="text-[17px] font-bold">{title}</p>
      {text && <p className="mt-1 text-[14px] text-[#64748B] max-w-xs">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, description, children, testid, wide }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`rounded-[22px] ${wide ? "sm:max-w-2xl" : "sm:max-w-md"} max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0`} data-testid={testid}>
        <DialogTitle className="text-[19px] font-bold">{title}</DialogTitle>
        {description ? <DialogDescription className="text-[14px] text-[#475569]">{description}</DialogDescription> : <DialogDescription className="sr-only">{title}</DialogDescription>}
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function InfoRows({ rows, testid }) {
  const shown = rows.filter((r) => r.value);
  if (!shown.length) return null;
  return (
    <div className="t-card px-4" data-testid={testid}>
      {shown.map(({ icon: Icon, label, value }, i) => (
        <div key={label} className={`flex items-center gap-3 py-4 ${i ? "border-t border-[#E2E8F0]" : ""}`}>
          <Icon className="w-5 h-5 text-[#64748B] shrink-0" strokeWidth={1.8} />
          <span className="text-[15px] font-semibold text-[#475569]">{label}</span>
          <span className="flex-1 text-right text-[15px] text-[#0F172A] break-words">{value}</span>
        </div>
      ))}
    </div>
  );
}

export const Page = ({ children, className = "" }) => <div className={`px-5 lg:px-10 pb-28 lg:pb-12 max-w-5xl lg:max-w-7xl mx-auto w-full fade-up ${className}`}>{children}</div>;

export function useIsDesktop() {
  const q = "(min-width: 1024px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}
